import { z } from "zod";

// TES-1131 (TES-1112 S5b, app 2.0.6) — "Kontakta Ella": the patient writes to
// Ella Support under one of the categories below.
//
// Drive 09C, "ÖVERORDNAT KANONISKT BESLUT — MEDICINSKA BESKED OCH KONTAKTVÄG":
// "Patienten använder den samlade ingången Kontakta Ella/Support och väljer kategori: teknisk hjälp; abonnemang och betalning; appen eller ett formulär; icke-akut medicinsk fråga. En icke-akut medicinsk fråga skickas till vårdteamets interna medicinska arbetskö och öppnar inte fri direktchatt med Kirill."
//
// The category travels with the message (`category` on the send body, kept
// in the message's payload as `supportCategory`). It is optional: 2.0.4 and
// 2.0.5 send none, and their messages behave exactly as before. A message in
// `medical_question` also opens a medical review case (TES-1120, source
// `support_thread`) for the care team.
//
// TES-1463 (Ö-469, Emelie 2026-10-20): "Bokning läggs till som egen kategori under Hjälp och kontakt, med befintliga bokningsbehörigheter och övriga ärendetyper bevarade."
// `booking` is a plain Support message like the other non-medical
// categories: it stays in the Support inbox and never reaches the coach.
// Only 2.1 and later send it; older builds keep sending the other four.
//
// TES-1662 (work list 2026-11-02, "BESLUTAD NAVIGATIONS- OCH HJÄLPMODELL"):
// "Beslutade kategorier är Jag förstår inte mitt nästa steg, Vårdmöte eller
// bokning, Teknisk hjälp, Abonnemang eller betalning, Appen eller ett
// formulär, Medicinsk fråga och Övrigt." The list is in that order. Two keys
// are new, `next_step` and `other`. Both are plain Support messages like the
// other non-medical categories ("Mottagande funktion routar vidare utan att
// patienten behöver skapa ett nytt ärende"). The server accepts them from
// every build; a build compiled before this list keeps sending its own five.
export const SUPPORT_CONTACT_CATEGORIES = [
  "next_step",
  "booking",
  "technical",
  "subscription_payment",
  "app_or_form",
  "medical_question",
  "other",
] as const;
export const supportContactCategorySchema = z.enum(SUPPORT_CONTACT_CATEGORIES);
export type SupportContactCategory = z.infer<
  typeof supportContactCategorySchema
>;

// 09C's labels, verbatim apart from the capital first letter a list option
// needs. The admin's Support inbox shows them; the app has the same words in
// its own locale files.
//
// TES-1463 (Ö-495/Ö-651, Emelie 2026-10-20): "Kategorin heter Medicinsk fråga."
// Only the label changes; the key stays `medical_question`, so routing to
// the care team's queue is unchanged.
//
// TES-1662 — the decided labels, verbatim ("Bokning förtydligas till
// Vårdmöte eller bokning"; "Abonnemang eller betalning"). The keys stay, so
// earlier messages read with the new words.
export const SUPPORT_CONTACT_CATEGORY_LABELS: Record<
  SupportContactCategory,
  string
> = {
  next_step: "Jag förstår inte mitt nästa steg",
  booking: "Vårdmöte eller bokning",
  technical: "Teknisk hjälp",
  subscription_payment: "Abonnemang eller betalning",
  app_or_form: "Appen eller ett formulär",
  medical_question: "Medicinsk fråga",
  other: "Övrigt",
};

// TES-1549 (Ö-625, Emelie 2026-10-21) — what a message is about, when Hem
// sent her to the form for it. One topic so far: "Jag vill återuppta
// behandlingen", from the card shown after a doctor's pause ended with no
// new start date. It opens the existing "Medicinsk fråga" ("Öppnar
// befintlig Medicinsk fråga"), so the topic rides only on that category. A
// key, never free text (the payload column is not encrypted). Only 2.1 and
// later send it.
//
// TES-1636 (TES-1632, Emelie 2026-10-28) — the second topic, from "Din
// behandling → Recept och läkemedel": "Under Recept och läkemedel finns
// länken Behöver du receptet tidigare? Kontakta Ella. Den leder till Kontakta
// Ella → Medicinsk fråga med ämnet Jag behöver receptet tidigare." No earlier
// prescription is promised; the doctor assesses it. Only 2.1 and later send
// it, and nothing about it is ever refused as a duplicate.
export const SUPPORT_CONTACT_TOPICS = [
  "treatment_restart",
  "prescription_earlier",
] as const;
export const supportContactTopicSchema = z.enum(SUPPORT_CONTACT_TOPICS);
export type SupportContactTopic = z.infer<typeof supportContactTopicSchema>;

// The subject each topic stands for, shown next to the category on the
// admin's Support inbox. The restart topic carries Hem's approved button text
// (Ö-625); the earlier-prescription topic its subject from the Drive doc.
export const SUPPORT_CONTACT_TOPIC_LABELS: Record<SupportContactTopic, string> =
  {
    treatment_restart: "Jag vill återuppta behandlingen",
    prescription_earlier: "Jag behöver receptet tidigare",
  };
